<script>
  import Tooltip from './Tooltip.svelte';
  import TextInput from '#lib/TextInput.svelte';
  import Container from '#lib/Container.svelte';
  import t from '#lib/i18n.svelte.js';
  import BlockGrid from '#lib/BlockGrid.svelte';
  import SearchSelect from '#lib/SearchSelect.svelte';
  import FormLabel from '#lib/FormLabel.svelte';
  import Text from '#lib/Text.svelte';
  import Image from '#lib/Image.svelte';
  import { fetch_entries, fetch_json, format_price } from '#lib/util.js';
  import EntrySelectorDrawer from '#lib/EntrySelectorDrawer.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import { getContext } from 'svelte';
  import ResourceModel from '#lib/ResourceModel.svelte.js';
  import { onMount } from 'svelte';
  import { 
    get_cancellation_notice_period_for_resource_type,
    get_booking_window_size_for_resource_type 
  } from '#lib/patch_util.js';
  import { page } from '$app/state';
  import { 
    RESOURCE_TYPE_BOOKING_OPTIONS, 
    RESOURCE_TYPE_PRICING_CONFIG,
    RESOURCE_TYPE_ENABLES_CREDITS_AND_DISCOUNTS 
  } from '#lib/app_config.js';
  import Icon from '#lib/Icon.svelte';
  import MeetingRoomLoxoneEditor from '#lib/MeetingRoomLoxoneEditor.svelte';

  let site_settings = getContext('site_settings');

  let { 
    entry, 
    is_editing,
    additional_entry_data
  } = $props();

  const booking_window_size = $derived(get_booking_window_size_for_resource_type(entry.resource_type));
  const cancellation_notice_period = $derived(get_cancellation_notice_period_for_resource_type(entry.resource_type));

  // Base booking type options
  const ALL_BOOKING_TYPE_OPTIONS = [
    { value: 'half_hourly', title: t('half_hourly') },
    { value: 'daily', title: t('daily') },
    { value: 'subscription', title: t('subscription') }
  ];

  // Filtered booking options based on selected resource type
  let booking_type_options = $derived.by(() => {
    if (!entry.resource_type) return [];
    
    const allowed_types = RESOURCE_TYPE_BOOKING_OPTIONS[entry.resource_type] || [];
    return ALL_BOOKING_TYPE_OPTIONS.filter(option => 
      allowed_types.includes(option.value)
    );
  });

  // Reset booking_type when resource_type changes
  $effect(() => {
    if (entry.is_new() && entry.resource_type) {
      entry.booking_type = booking_type_options[0]?.value || null;
    }
  });

  let available_resource_types = $state(ResourceModel.SORTED_RESOURCE_TYPE_OPTIONS);

  // Computed options that exclude already the flex_desk resource_type if it was used already
  let resource_type_options = $derived(available_resource_types.map(type => ({
    value: type,
    title: t(type)
  })));

  async function load_available_resource_types() {
    try {
      const resources = await fetch_json('GET', '/api/resources');
      if (!resources) return;

      // Get all resource types that are already in use
      const used_types = new Set(resources.map(r => r.resource_type));
      
      // Filter out 'flex_desk' if it's already used
      if (used_types.has('flex_desk')) {
        available_resource_types = ResourceModel.SORTED_RESOURCE_TYPE_OPTIONS.filter(
          type => type !== 'flex_desk'
        );
      }
    } catch (error) {
      console.error('Error loading resources:', error);
    }
  }

  onMount(() => {
    // Todo / Question: Should we run this on the server instead? But where?
    load_available_resource_types();
  });

  // Fetch the image entries referenced by the resource entry
  $effect.pre(() => {
    fetch_entries(entry.images_multiref, {as_preview: false}).then(images => {
      entry.images = images;
    });
  });

</script>

<BlockGrid class='resource-entry'>
  <header class='gap-x g-gutter mbs m-10'>
    <div class='image-container'>
      {#if is_editing}
        <EntrySelectorDrawer
          entry_type='image'
          excluded_ids={entry.images_multiref || []}
          label={t('images')} 
          icon='polaroid-stack'
          variant='brand elevated'
          multi_select={true}
          confirm_label={t('add_selected_entry')}
          confirm_label_plural={t('add_selected_entries', { count: '{{count}}' })}
          onconfirm={(entry_ids) => {
            if (!Array.isArray(entry.images_multiref)) {
              entry.images_multiref = [];
            }

            const existing_image_ids = new Set(entry.images_multiref);
            for (const entry_id of entry_ids) {
              if (!existing_image_ids.has(entry_id)) {
                entry.images_multiref.push(entry_id);
                existing_image_ids.add(entry_id);
              }
            }
          }}
        />
      {/if}

      <div class='image-list'>
        {#if entry.images.length > 0}
          {#each entry.images as image}
            <Image {...image} />
          {/each}
        {:else if is_editing}
          <Image />
        {/if}
      </div>
    </div>
    
    <div class='flex-column gap-y g-6 items-start'>
      
      <div class='flex-column gap-y g-5 relative items-start'>
        <div class='form-row gap-x g-5 items-center'>
          {#if is_editing}
            <FormLabel class='no-padding' for='booking_type'>{t('resource_type')}</FormLabel>
          {/if}
          {#if !is_editing || !entry.is_new()}
            <p class='text-preamble leading-trim'>{t(entry.resource_type)}</p>
          {:else if is_editing && entry.is_new()}
            <SearchSelect
              search_input_id='resource_type'
              options={resource_type_options}
              bind:value={entry.resource_type}
              placeholder={t('select_resource_type')}
              variant='brand outlined'
              allow_null={false}
              enable_search={false}
              show_search_icon={false}
            />
          {/if}
        </div>
        <div class='relative'>
          <Text
            bind:content={entry.title}
            variant='heading2'
            editable={is_editing}
            placeholder={t('title')}
          />
        </div>
      </div>

      <!-- Pricing -->
      {#if !is_editing}
        <div class='flex-row gap-x g-5 items-center'>
          <!-- Display price per unit for non admins -->
          {#if additional_entry_data?.pricing?.price !== undefined}
            <div class='flex-column'>
              <p class='text-body'>
                {format_price(additional_entry_data.pricing.price, 'EUR', 'de-DE')}&thinsp;/&thinsp;
                {t(RESOURCE_TYPE_PRICING_CONFIG[entry.resource_type].price_unit)}
              </p>
              {#if additional_entry_data.pricing.max_price}
                <p class='text-body text-3'>
                  {t('max')} {format_price(additional_entry_data.pricing.max_price, 'EUR', 'de-DE')}&thinsp;/&thinsp;
                  {t(RESOURCE_TYPE_PRICING_CONFIG[entry.resource_type].max_price_unit)}
                </p>
              {/if}
              <p class='text-caption text-3'>{t('plus_vat')}</p>
            </div>
          {:else}
            <p class='text-body text-3'>{t('pricing_not_configured')}</p>
          {/if}
          {#if page.data.current_user?.role === 'admin'}
            <a href={`/pricing-and-capacity`}>
              <ButtonLabel start_icon='pencil' variant='brand {site_settings.secondary_control_surface}' align={site_settings.control_surface_align}>
                {t('pricing_and_capacity')}
              </ButtonLabel>
            </a>
          {/if}
        </div>
      {/if}

      <!-- Discounts and credits -->
      {#if !is_editing}
        <!-- additional_entry_data.pricing is missing when no pricing row exists for the current
             month — an unguarded deref here crashed the public entry page during SSR -->
        {#if RESOURCE_TYPE_ENABLES_CREDITS_AND_DISCOUNTS.includes(entry.resource_type) && additional_entry_data?.pricing}
          <div class='flex-column gap-y g-2 mts m-5'>
            <p class='text-body'>{t('subscriber_benefits')}:</p>
            {#if additional_entry_data.pricing.meeting_room_credit_allowance > 0}
              <p class='text-body flex-row gap-x g-2 items-center'>
                <Icon name='check' />
                {t('credit_allowance_for_meeting_room')}: {format_price(additional_entry_data.pricing.meeting_room_credit_allowance, 'EUR', 'de-DE')}&thinsp;/&thinsp;{t('month')}
              </p>
            {/if}
            {#if additional_entry_data.pricing.meeting_room_discount_percentage > 0}
              <p class='text-body flex-row gap-x g-2 items-center'>
                <Icon name='check' />
                {t('discount_for_meeting_room')}: {additional_entry_data.pricing.meeting_room_discount_percentage}%
              </p>
            {/if}
          </div>
        {/if}
      {/if}

      <!-- Cancellation notice -->
      {#if cancellation_notice_period}
        <p class='text-body text-3'>
          {t('cancellation_notice', {months: cancellation_notice_period, unit: cancellation_notice_period === 1 ? t('month') : t('months')})}
        </p>
      {/if}


      <div class='form-row gap-x g-7 items-center'>
        {#if is_editing}
          {#if entry.is_new() && booking_type_options.length > 0}
            <FormLabel class='no-padding' for='booking_type'>{t('booking_type')}</FormLabel>
            {#if booking_type_options.length > 1}
              <SearchSelect
                search_input_id='booking_type'
                options={booking_type_options}
                bind:value={entry.booking_type}
                variant='brand outlined'
                allow_null={false}
                enable_search={false}
                show_search_icon={false}
                disabled={!entry.resource_type}
              />
            {:else}
              <p class='text-preamble'>{t(entry.booking_type)}</p>
            {/if}
          {:else if !entry.is_new()}
            <FormLabel class='no-padding' for='booking_type'>{t('booking_type')}</FormLabel>
            <p class='text-preamble'>{t(entry.booking_type)}</p>
          {/if}
        {:else}
          <a href={`/book/${entry.slug}-${entry.entry_id}`}>
            <ButtonLabel 
              variant='primary {site_settings.primary_control_surface}'
              align={site_settings.control_surface_align}
              start_icon='arrow-right-tail'
            >
              {t('book_now')}
            </ButtonLabel>
          </a>
        {/if}
      </div>

      <MeetingRoomLoxoneEditor {entry} {is_editing} />

      {#if entry.booking_type === 'daily' && is_editing}
        <div class='form-row gap-x g-5 items-start'>
          <FormLabel class='no-padding' for='max_individual_daily_bookings_per_day'>{t('max_individual_daily_bookings_per_day')}</FormLabel>
          <TextInput
            id='max_individual_daily_bookings_per_day'
            type='number'
            min='0'
            bind:value={entry.max_individual_daily_bookings_per_day}
            placeholder={t('unlimited')}
          />
          <Tooltip description={t('max_individual_daily_bookings_per_day_tooltip')} />
        </div>

        <div class='form-row gap-x g-5 items-start'>
          <FormLabel class='no-padding' for='max_individual_daily_bookings_per_month'>{t('max_individual_daily_bookings_per_month')}</FormLabel>
          <TextInput
            id='max_individual_daily_bookings_per_month'
            type='number'
            min='0'
            bind:value={entry.max_individual_daily_bookings_per_month}
            placeholder={t('unlimited')}
          />
          <Tooltip description={t('max_individual_daily_bookings_per_month_tooltip')} />
        </div>
      {/if}

    </div>
  </header>
  <main class='mbs m-10'>
    <Container 
      container={entry.body} 
      bind:this={entry.body.ref} 
    />
  </main>
</BlockGrid>


<style lang='postcss'>
  header {
    grid-column: var(--dynamic-island-track-start) / var(--dynamic-island-track-end);
    display: grid;
    grid-template-columns: 1fr 1fr;
    > * {
      min-width: 0;
    }
  }
  .image-container {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    > :global(button), .image-list {
      grid-area: 1/1;
    }
    > :global(button) {
      align-self: start;
      justify-self: end;
      margin-block-start: var(--size-3);
      margin-inline-end: var(--size-3);
      :global(.button-label) {
        --color-control: var(--color-default-background);
      }
    }

  }
  main {
    grid-column: 1 / -1;
  }
</style>
